import React, { ReactNode } from "react";
import { createWeb3Modal } from "@web3modal/wagmi/react";
import { arbitrum } from "viem/chains";
import { alchemyProvider } from "wagmi/providers/alchemy";
import { infuraProvider } from "wagmi/providers/infura";
import { publicProvider } from "wagmi/providers/public";
import { CoinbaseWalletConnector } from "wagmi/connectors/coinbaseWallet";
import { SafeConnector } from "wagmi/connectors/safe";
import { InjectedConnector } from "wagmi/connectors/injected";
import { WalletConnectConnector } from "wagmi/connectors/walletConnect";
import { walletConnectProvider, EIP6963Connector } from "@web3modal/wagmi";
import { WagmiConfig, configureChains, createConfig, createStorage } from "wagmi";
import { useIgnoreENSUnsupportedError } from "@/hooks/useIgnoreENSUnsupportedError";
import shellIcon from "@/assets/icons/shell.svg";

export const projectId = "1a0918d9c1353afb69eeedbbbcb3eb0a"; // walletconnect cloud
export const infuraId = "6a2e4095949b446d9b2d93a50182519a";
export const alchemyId = "vqPEBfzKazq958r46AU6cUf4Jxldxs9L";

function createLimitedStorage(storage: Storage, maxSize: number): Storage {
  return {
    get length() {
      return storage.length;
    },
    getItem: (key) => {
      return storage.getItem(key);
    },
    setItem: (key, value) => {
      if (value && value.length > maxSize) {
        throw new Error(
          `Storage limit exceeded! Max allowed size is ${maxSize} characters.`
        );
      }
      storage.setItem(key, value);
    },
    removeItem: (key) => {
      storage.removeItem(key);
    },
    clear: () => {
      storage.clear();
    },
    key: (index) => {
      return storage.key(index);
    },
  };
}

const limitedLocalStorage = createLimitedStorage(window.localStorage, 5000);
const wagmiStorage = createStorage({ storage: limitedLocalStorage });

const { chains, publicClient } = configureChains(
  [arbitrum],
  [
    alchemyProvider({ apiKey: alchemyId }),
    infuraProvider({ apiKey: infuraId }),
    walletConnectProvider({ projectId }),
    publicProvider(),
  ]
);

const metadata = {
  name: "Shell Protocol",
  description: "Where simplicity meets DeFi. Powerful one-click transactions, unbeatably capital-efficient AMMs, and a modular developer experience.",
  url: "https://shellprotocol.io/",
  icons: [shellIcon],
};

const wagmiConfig = createConfig({
  autoConnect: true,
  connectors: [
    new WalletConnectConnector({
      chains,
      options: { projectId, showQrModal: false, metadata },
    }),
    new EIP6963Connector({ chains }),
    new InjectedConnector({
      chains,
      options: {
        shimDisconnect: true,
      },
    }),
    new CoinbaseWalletConnector({
      chains,
      options: { appName: metadata.name },
    }),
    new SafeConnector({
      chains: chains,
      options: {
        allowedDomains: [/gnosis-safe.io$/, /app.safe.global$/],
        debug: false,
      },
    }),
  ],
  publicClient,
  storage: wagmiStorage,
});

createWeb3Modal({
  wagmiConfig,
  projectId,
  chains,
  defaultChain: arbitrum,
  themeVariables: {
    "--w3m-color-mix": "#0a0e27",
    "--w3m-color-mix-strength": 20,
    "--w3m-font-family": "Inter",
    "--w3m-accent": "#00bdff",
    "--w3m-font-size-master": "12px",
    // "--w3m-border-radius-master": "16px"
  },
});

export function WagmiProvider({ children }: { readonly children: ReactNode }) {
  useIgnoreENSUnsupportedError();

  return <WagmiConfig config={wagmiConfig}>{children}</WagmiConfig>;
}
